/**
 * THE LIGHTBULB, ITEM FIRST (owner, 20 Oct).
 *
 *   1. "Before you walk in": at most 5 short lines about THIS item (crew, stairs at both
 *      ends, what the seller reported, access notes, the published weight, the top lesson).
 *   2. This item: its own facts, the seller's write-up, the manufacturer specs (verbatim),
 *      listing photos and video, the seller and buyer path media.
 *   3. What drivers before you ran into: notes from finished jobs of the same model.
 *   4. Category guide, collapsed: the generic steps, specs, precautions and videos that used
 *      to fill the whole sheet and read the same on every job of the category.
 *
 * One component for every screen that opens the bulb, so they cannot drift apart again.
 * Everything server-side is fetched here when the sheet opens (the brief is usually already
 * prefetched by the job screen) and fails soft: with nothing from the server the sheet is
 * the category guide, opened, exactly as before.
 *
 * 🚨 Drivers never see money here. The brief is scrubbed server-side; nothing on this sheet
 * reads a payout, price or fee field from the job.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, Image, ScrollView, Linking, Dimensions } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { RD } from '../theme/colors';
import { api } from '../services/api';
import { fetchJobBrief, type JobBrief } from '../services/jobBrief';
import { getDriverTips, getCategoryDimensions, getCategoryWeight } from '../utils/driverTips';
import { PathMediaSections } from './PathMediaSections';
import ImageLightbox from './ImageLightbox';

type Instructions = { specs?: string[]; precautions?: string[]; steps?: string[]; videos?: { title: string; url: string }[] } | null;

const specsCache = new Map<string, Promise<Array<{ spec: string; line: string }>>>();
const instrCache = new Map<string, Promise<Instructions>>();

const parseList = (v: any): string[] => {
  if (!v) return [];
  if (Array.isArray(v)) return v.filter(Boolean);
  try { const p = JSON.parse(v); return Array.isArray(p) ? p.filter(Boolean) : []; } catch { return []; }
};
const isVideoUrl = (u: string) => /\.(mp4|mov|webm|m4v|mkv|avi|quicktime)(\?|$)/i.test(u || '') || (u || '').includes('/video/upload/');

export function LightbulbBody({ job, note }: { job: any; note?: string | null }) {
  const jobId: string = job?.id || '';
  const [brief, setBrief] = useState<JobBrief | null>(null);
  const [briefLoaded, setBriefLoaded] = useState(false);
  const [productSpecs, setProductSpecs] = useState<Array<{ spec: string; line: string }>>([]);
  const [instr, setInstr] = useState<Instructions>(null);
  const [guideOpen, setGuideOpen] = useState(false);
  const [showDetailed, setShowDetailed] = useState(false);
  const [lightbox, setLightbox] = useState<{ list: string[]; index: number } | null>(null);

  useEffect(() => {
    if (!jobId) return;
    let off = false;
    fetchJobBrief(jobId).then((b) => { if (!off) { setBrief(b); setBriefLoaded(true); } });
    // 🚨 This used to call `/api/jobs/...` on a client whose base URL already ends in /api,
    // so it asked for /api/api/jobs/:id/product-specs and got a 404 every time: the
    // manufacturer specs never reached a single driver.
    if (!specsCache.has(jobId)) specsCache.set(jobId, api.get(`/jobs/${jobId}/product-specs`).then((r) => (Array.isArray(r.data?.specs) ? r.data.specs : [])).catch(() => []));
    specsCache.get(jobId)!.then((s) => { if (!off) setProductSpecs(s); });
    const slug = job?.categorySlug;
    if (slug) {
      const k = `${slug}|${job?.productTitle || ''}`;
      if (!instrCache.has(k)) instrCache.set(k, api.get(`/category-instructions/${encodeURIComponent(slug)}`, { params: { productTitle: job?.productTitle || '' } }).then((r) => r.data || null).catch(() => null));
      instrCache.get(k)!.then((d) => { if (!off) setInstr(d); });
    }
    return () => { off = true; };
  }, [jobId, job?.categorySlug, job?.productTitle]);

  const fallbackTips: any = useMemo(() => getDriverTips(job?.productTitle || '', job?.categorySlug || ''), [job?.productTitle, job?.categorySlug]);
  const allMedia = parseList(job?.productImages);
  const photos = allMedia.filter((u) => !isVideoUrl(u));
  const videos = allMedia.filter((u) => isVideoUrl(u));
  const listingVideos = parseList(job?.listingVideos);

  const liveSteps = (instr?.steps || []).filter(Boolean);
  const liveSpecs = (instr?.specs || []).filter(Boolean);
  const livePrecautions = (instr?.precautions || []).filter(Boolean);
  const liveVideos = (instr?.videos || []).filter((v) => v && v.url);
  const steps: string[] = liveSteps.length ? liveSteps : (fallbackTips?.tips || []);
  const howTo = liveVideos.length ? liveVideos : (fallbackTips?.videos || []);
  const detailed: string[] = fallbackTips?.detailedTips || [];
  // The category's typical size and weight only when the exact model's figures are unknown.
  const catDims = productSpecs.length ? null : getCategoryDimensions(job?.productTitle || '', job?.categorySlug || '');
  const catWeight = productSpecs.length ? null : getCategoryWeight(job?.productTitle || '', job?.categorySlug || '');
  const hasGuide = steps.length > 0 || liveSpecs.length > 0 || livePrecautions.length > 0 || howTo.length > 0 || detailed.length > 0 || !!catDims || !!catWeight;

  // Crew is always known on the job, so the walk-in block is never empty even before (or
  // without) the brief: the server's lines replace this once they arrive.
  const crewFallback = (() => {
    const total = Number(job?.helpersNeeded) || 0;
    if (!total) return null;
    const h = Math.max(0, total - 1);
    return h ? `${total} people: 1 driver + ${h} helper${h > 1 ? 's' : ''}` : '1 person: you can do this one solo';
  })();
  const walkIn = brief?.walkIn?.length ? brief.walkIn : (crewFallback ? [crewFallback] : []);
  const itemFacts = (brief?.itemFacts || []).filter((f) => !walkIn.includes(f));
  const lessons = brief?.lessons || [];
  const lessonsFromCategory = lessons.length > 0 && lessons.every((l) => l.scope === 'category');
  // With no brief at all the guide is the only thing to read, so it starts open.
  const guideShown = guideOpen || (briefLoaded && !brief?.walkIn?.length && !itemFacts.length && !lessons.length && !job?.itemDescription && !productSpecs.length);

  return (
    <View>
      {!!note && (
        <View style={s.note}>
          <Ionicons name="information-circle" size={18} color={RD.info} />
          <Text style={s.noteText}>{note}</Text>
        </View>
      )}

      {/* 1. Before you walk in */}
      {walkIn.length > 0 && (
        <View style={s.walkCard}>
          <Text style={s.walkTitle}>Before you walk in</Text>
          {walkIn.slice(0, 5).map((l, i) => (
            <View key={i} style={[s.walkRow, i > 0 && { marginTop: 8 }]}>
              <View style={s.walkDot} />
              <Text style={s.walkText}>{l}</Text>
            </View>
          ))}
        </View>
      )}

      {/* 2. This item */}
      <View style={s.section}>
        <View style={s.sectionHead}>
          <Ionicons name="cube-outline" size={16} color={RD.info} />
          <Text style={s.sectionTitle}>This item</Text>
        </View>
        {itemFacts.length > 0 && (
          <View style={s.card}>
            {itemFacts.map((f, i) => (
              <View key={i} style={[s.row, i > 0 && { marginTop: 9 }]}>
                <Ionicons name="ellipse" size={6} color={RD.info} style={{ marginTop: 7 }} />
                <Text style={s.rowText}>{f}</Text>
              </View>
            ))}
          </View>
        )}
        {/* The live listing: what the buyer was promised, accessories and all (owner, 3 Nov). */}
        {!!brief?.listingUrl && (
          <TouchableOpacity activeOpacity={0.85} onPress={() => Linking.openURL(brief.listingUrl as string).catch(() => {})} style={[s.videoRow, { marginTop: itemFacts.length ? 8 : 0 }]}>
            <Ionicons name="pricetag-outline" size={22} color={RD.maroon} />
            <Text style={s.videoText}>Open the live listing to check what is included</Text>
            <Ionicons name="open-outline" size={16} color={RD.textMuted} />
          </TouchableOpacity>
        )}
        {!!job?.itemDescription && (
          <View style={[s.card, { backgroundColor: RD.infoSoft, borderColor: RD.infoSoft, marginTop: itemFacts.length ? 8 : 0 }]}>
            <Text style={s.body}>{job.itemDescription}</Text>
          </View>
        )}
        {/* Manufacturer specs. 🚨 Every line arrives pre-worded and pre-attributed by the
            server and is rendered VERBATIM: no reformatting, never strip the "verify on site"
            tail. A published spec is not a measurement of the unit on the truck. */}
        {productSpecs.length > 0 && (
          <View style={{ marginTop: 10 }}>
            <Text style={s.subTitle}>Manufacturer specs</Text>
            {productSpecs.map((ps, i) => (
              <View key={i} style={[s.card, { marginTop: i ? 6 : 0, paddingVertical: 10 }]}>
                <Text style={s.body}>{ps.line}</Text>
              </View>
            ))}
          </View>
        )}
        {(photos.length > 0 || videos.length > 0) && (
          <View style={{ marginTop: 10 }}>
            <Text style={s.subTitle}>Listing photos{videos.length ? ' and video' : ''}</Text>
            {photos.length > 0 && (
              <ScrollView horizontal showsHorizontalScrollIndicator={false} style={{ marginHorizontal: -4 }}>
                {photos.map((uri, i) => (
                  <TouchableOpacity key={i} activeOpacity={0.85} onPress={() => setLightbox({ list: photos, index: i })}>
                    <Image source={{ uri }} style={{ width: Dimensions.get('window').width * 0.6, height: Dimensions.get('window').width * 0.45, borderRadius: 12, marginHorizontal: 4 }} resizeMode="cover" />
                  </TouchableOpacity>
                ))}
              </ScrollView>
            )}
            {[...videos, ...listingVideos.filter((u) => !videos.includes(u))].map((uri, i) => (
              <TouchableOpacity key={`v${i}`} activeOpacity={0.85} onPress={() => Linking.openURL(uri).catch(() => {})} style={[s.videoRow, { marginTop: 8 }]}>
                <Ionicons name="play-circle" size={26} color={RD.maroon} />
                <Text style={s.videoText}>Watch listing video{videos.length + listingVideos.length > 1 ? ` ${i + 1}` : ''}</Text>
                <Ionicons name="open-outline" size={16} color={RD.textMuted} />
              </TouchableOpacity>
            ))}
          </View>
        )}
        <View style={{ marginTop: 10 }}>
          <PathMediaSections job={job} />
        </View>
      </View>

      {/* 3. What drivers before you ran into */}
      {lessons.length > 0 && (
        <View style={s.section}>
          <View style={s.sectionHead}>
            <Ionicons name="people-outline" size={16} color={RD.amber} />
            <Text style={s.sectionTitle}>{lessonsFromCategory ? 'From other jobs in this category' : 'What drivers before you ran into'}</Text>
          </View>
          <View style={[s.card, { backgroundColor: RD.amberSoft, borderColor: RD.amberSoft }]}>
            {lessons.map((l, i) => (
              <View key={i} style={i > 0 ? { marginTop: 11 } : undefined}>
                <Text style={s.rowText}>{l.text}</Text>
                {!!l.when && <Text style={s.when}>{l.when}</Text>}
              </View>
            ))}
          </View>
        </View>
      )}

      <View style={s.callout}>
        <Ionicons name="shield-checkmark" size={18} color={RD.amber} />
        <Text style={s.calloutText}>Always do a thorough inspection before moving anything.</Text>
      </View>

      {/* 4. Category guide, collapsed */}
      {hasGuide && (
        <View style={s.section}>
          <TouchableOpacity onPress={() => setGuideOpen(!guideShown)} activeOpacity={0.7} style={s.guideToggle}>
            <Ionicons name="book-outline" size={17} color={RD.textSec} />
            <Text style={s.guideToggleText}>Category guide</Text>
            <Text style={s.guideHint}>same for every job of this kind</Text>
            <Ionicons name={guideShown ? 'chevron-up' : 'chevron-down'} size={16} color={RD.textSec} />
          </TouchableOpacity>
          {guideShown && (
            <View style={{ marginTop: 10 }}>
              {(catDims || catWeight) && (
                <View style={[s.card, { marginBottom: 10 }]}>
                  <Text style={s.subTitle}>Typical for the category, not this model</Text>
                  {!!catDims && <Text style={s.body}>Size: {catDims}</Text>}
                  {!!catWeight && <Text style={s.body}>Weight: {catWeight}</Text>}
                </View>
              )}
              {steps.length > 0 && (
                <View style={[s.card, { marginBottom: 10 }]}>
                  <Text style={s.subTitle}>How to move it</Text>
                  {steps.map((t, i) => (
                    <View key={i} style={[s.row, i > 0 && { marginTop: 10 }]}>
                      <View style={s.stepNum}><Text style={s.stepNumText}>{i + 1}</Text></View>
                      <Text style={s.rowText}>{t}</Text>
                    </View>
                  ))}
                </View>
              )}
              {liveSpecs.length > 0 && (
                <View style={[s.card, { marginBottom: 10 }]}>
                  <Text style={s.subTitle}>Specs</Text>
                  {liveSpecs.map((t, i) => <Text key={i} style={[s.body, i > 0 && { marginTop: 6 }]}>{t}</Text>)}
                </View>
              )}
              {livePrecautions.length > 0 && (
                <View style={[s.card, { marginBottom: 10, backgroundColor: RD.amberSoft, borderColor: RD.amberSoft }]}>
                  <Text style={s.subTitle}>Precautions</Text>
                  {livePrecautions.map((t, i) => <Text key={i} style={[s.body, i > 0 && { marginTop: 6 }]}>{t}</Text>)}
                </View>
              )}
              {howTo.map((v: { title: string; url: string }, i: number) => (
                <TouchableOpacity key={`h${i}`} style={[s.videoRow, { marginBottom: 8 }]} onPress={() => Linking.openURL(v.url).catch(() => {})} activeOpacity={0.7}>
                  <Ionicons name="play-circle" size={26} color={RD.danger} />
                  <Text style={s.videoText}>{v.title}</Text>
                  <Ionicons name="open-outline" size={16} color={RD.g30} />
                </TouchableOpacity>
              ))}
              {detailed.length > 0 && !showDetailed && (
                <TouchableOpacity onPress={() => setShowDetailed(true)} style={s.guideMore} activeOpacity={0.7}>
                  <Text style={s.guideMoreText}>Full guide</Text>
                  <Ionicons name="chevron-down" size={15} color={RD.info} />
                </TouchableOpacity>
              )}
              {showDetailed && detailed.map((tip, i) => {
                const m = tip.match(/^([A-Z][A-Z\s\-&/]+(?:\d*)?):?\s*/);
                return (
                  <View key={`d${i}`} style={{ marginTop: i ? 12 : 4 }}>
                    {!!m && <Text style={s.detailHead}>{m[1].trim()}</Text>}
                    <Text style={s.body}>{m ? tip.slice(m[0].length) : tip}</Text>
                  </View>
                );
              })}
            </View>
          )}
        </View>
      )}

      <ImageLightbox images={lightbox?.list || []} index={lightbox ? lightbox.index : null} onClose={() => setLightbox(null)} />
    </View>
  );
}

/**
 * Just the item-first top of the sheet ("Before you walk in" + the past-job lessons), for a
 * screen that keeps its own sheet below it (the pre-dispatch checklist's lightbulb).
 */
export function WalkInCard({ jobId }: { jobId: string | null | undefined }) {
  const [brief, setBrief] = useState<JobBrief | null>(null);
  useEffect(() => {
    if (!jobId) return;
    let off = false;
    fetchJobBrief(jobId).then((b) => { if (!off) setBrief(b); });
    return () => { off = true; };
  }, [jobId]);
  if (!brief || (!brief.walkIn.length && !brief.lessons.length)) return null;
  const fromCategory = brief.lessons.length > 0 && brief.lessons.every((l) => l.scope === 'category');
  return (
    <View>
      {brief.walkIn.length > 0 && (
        <View style={s.walkCard}>
          <Text style={s.walkTitle}>Before you walk in</Text>
          {brief.walkIn.slice(0, 5).map((l, i) => (
            <View key={i} style={[s.walkRow, i > 0 && { marginTop: 8 }]}>
              <View style={s.walkDot} />
              <Text style={s.walkText}>{l}</Text>
            </View>
          ))}
        </View>
      )}
      {brief.lessons.length > 0 && (
        <View style={s.section}>
          <View style={s.sectionHead}>
            <Ionicons name="people-outline" size={16} color={RD.amber} />
            <Text style={s.sectionTitle}>{fromCategory ? 'From other jobs in this category' : 'What drivers before you ran into'}</Text>
          </View>
          <View style={[s.card, { backgroundColor: RD.amberSoft, borderColor: RD.amberSoft }]}>
            {brief.lessons.map((l, i) => (
              <View key={i} style={i > 0 ? { marginTop: 11 } : undefined}>
                <Text style={s.rowText}>{l.text}</Text>
                {!!l.when && <Text style={s.when}>{l.when}</Text>}
              </View>
            ))}
          </View>
        </View>
      )}
    </View>
  );
}

const s = StyleSheet.create({
  note:        { flexDirection: 'row', alignItems: 'center', gap: 8, padding: 12, borderRadius: 12, backgroundColor: RD.infoSoft, marginBottom: 12 },
  noteText:    { flex: 1, fontSize: 14, fontWeight: '700', color: RD.info },
  walkCard:    { backgroundColor: RD.surface, borderRadius: 16, borderWidth: 2, borderColor: RD.ink, padding: 15, marginBottom: 18 },
  walkTitle:   { fontSize: 15, fontWeight: '800', color: RD.text, marginBottom: 10 },
  walkRow:     { flexDirection: 'row', gap: 10, alignItems: 'flex-start' },
  walkDot:     { width: 7, height: 7, borderRadius: 4, backgroundColor: RD.ink, marginTop: 7 },
  walkText:    { flex: 1, fontSize: 15, fontWeight: '600', color: RD.text, lineHeight: 21 },
  section:     { marginBottom: 16 },
  sectionHead: { flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 9 },
  sectionTitle:{ fontSize: 14, fontWeight: '700', color: RD.text },
  subTitle:    { fontSize: 12.5, fontWeight: '700', color: RD.textSec, marginBottom: 6 },
  card:        { backgroundColor: RD.surface, borderRadius: 14, borderWidth: 1, borderColor: RD.border, padding: 14 },
  row:         { flexDirection: 'row', gap: 10, alignItems: 'flex-start' },
  rowText:     { flex: 1, fontSize: 13.5, color: RD.text, lineHeight: 19 },
  body:        { fontSize: 13, color: RD.textSec, lineHeight: 20 },
  when:        { fontSize: 11.5, color: RD.textMuted, marginTop: 2 },
  callout:     { flexDirection: 'row', alignItems: 'center', gap: 10, padding: 12, borderRadius: 12, backgroundColor: RD.amberSoft, marginBottom: 16 },
  calloutText: { flex: 1, fontSize: 13, fontWeight: '700', color: RD.amber, lineHeight: 18 },
  videoRow:    { flexDirection: 'row', alignItems: 'center', gap: 10, padding: 12, backgroundColor: RD.surface, borderWidth: 1, borderColor: RD.border, borderRadius: 12 },
  videoText:   { flex: 1, fontSize: 14, fontWeight: '600', color: RD.text },
  guideToggle: { flexDirection: 'row', alignItems: 'center', gap: 8, paddingVertical: 13, paddingHorizontal: 14, borderRadius: 14, backgroundColor: RD.g05, minHeight: 48 },
  guideToggleText: { fontSize: 14.5, fontWeight: '700', color: RD.text },
  guideHint:   { flex: 1, fontSize: 11.5, color: RD.textMuted },
  guideMore:   { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 6, paddingVertical: 10, minHeight: 44 },
  guideMoreText: { fontSize: 14, fontWeight: '700', color: RD.info },
  stepNum:     { width: 22, height: 22, borderRadius: 11, backgroundColor: RD.infoSoft, justifyContent: 'center', alignItems: 'center' },
  stepNumText: { fontSize: 11.5, fontWeight: '700', color: RD.info },
  detailHead:  { fontSize: 12.5, fontWeight: '800', color: RD.info, letterSpacing: 0.3, marginBottom: 3 },
});
